import React, { useEffect, useMemo, useState } from 'react'

import { IntegrationEmail, IntegrationEmailThread } from '../../../models/Email'
import { useLastContactEmailFromThread } from '../Table/hooks'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectCurrentUserEmailIntegration, selectEmailContacts } from '../../../store/emailHub/emailUsers'
import { EmailResponseInitialization } from '../Initialization/EmailResponseInitialization'
import { EmailResponsePoller } from '../Poller/EmailResponsePoller'
import {
  selectEmailResponse,
  selectEmailResponseExecutionLoading,
  selectEmailResponseLoading,
  selectEmailResponsePollingEnabled,
  selectShouldPollEmailResponse,
  startEmailResponsePolling,
  stopEmailResponsePolling,
} from '../../../store/emailHub/emailResponse'
import {
  selectEmailMetadataPollingEnabled,
  selectShouldPollEmailResponseTasks,
  selectShouldPollMetadata,
  startEmailMetadataPolling,
  stopEmailMetadataPolling,
} from '../../../store/emailHub/emailMetadata'
import { Col, Collapse, Container, Row } from 'react-bootstrap'
import classNames from 'classnames'
import { UserAvatar } from '../../../components/UserAvatar/UserAvatar'
import { UserTag } from '../../../apps/professional-app/fa/UserTag'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { EmailResponse } from './EmailResponse'
import { ExternalUserServiceIntegration, User } from '../../../models/User'
import { EmailMetadata } from '../../../models/EmailMetadata'
import { EmailResponseProcessing } from '../Common/EmailResponseProcessing'
import { getInitials } from '../../utils'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { EmailResponseRecipients } from './EmailResponseRecipients'
import { EmailResponseChat } from './EmailResponseChat'
import { EmailResponseTasks } from './EmailResponseTasks'
import { EmailResponseContacts } from './EmailResponseContacts'
import { startTaskPolling, stopTaskPolling } from '../../../store/task/task'
import { isEmailResponseNoResult } from './utils'
import { PluginPromo, PromoType } from '../../../components/Promo/Plugin/PluginPromo'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { IntegrationServiceId } from '../../../common/integrations'
import { EmailThreadAttachments } from './EmailThreadAttachments'

export interface EmailResponseViewProps {
  thread: IntegrationEmailThread
}

export const EmailResponseView: React.FC<EmailResponseViewProps> = ({ thread }) => {
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)

  const { lastEmailByContact, lastEmailByContactMetadata } = useLastContactEmailFromThread(thread)
  const lastEmailAnswered =
    emailIntegration &&
    lastEmailByContact &&
    thread.emails.some((email) => email.sender.email === emailIntegration.email && email.createdAt > lastEmailByContact.createdAt)

  if (!emailIntegration || !lastEmailByContact) {
    return null
  }

  if (lastEmailAnswered) {
    return <EmailResponseProcessing emailConnector={emailIntegration.serviceId} email={lastEmailByContact} metadata={lastEmailByContactMetadata} />
  }

  return <EmailResponseBody email={lastEmailByContact} metadata={lastEmailByContactMetadata} />
}

interface EmailResponseBodyProps {
  email: IntegrationEmail
  metadata: EmailMetadata | null
}

const EmailResponseBody: React.FC<EmailResponseBodyProps> = ({ email, metadata }) => {
  const dispatch = useAppDispatch()

  const shouldPollMetadata = useAppSelector((state) => selectShouldPollMetadata(state, email.externalId))
  const shouldPollEmailResponse = useAppSelector((state) => selectShouldPollEmailResponse(state, email.externalId))
  const shouldPollTasks = useAppSelector((state) => selectShouldPollEmailResponseTasks(state, email.externalId))

  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)
  const contacts = useAppSelector(selectEmailContacts)

  const contact = contacts.find((contact) => contact.email?.toLowerCase() === email.sender.email.toLowerCase())

  useEffect(() => {
    if (shouldPollEmailResponse) {
      dispatch(startEmailResponsePolling())
    } else {
      dispatch(stopEmailResponsePolling())
    }
  }, [dispatch, shouldPollEmailResponse])

  useEffect(() => {
    if (shouldPollMetadata) {
      dispatch(startEmailMetadataPolling())
    } else {
      dispatch(stopEmailMetadataPolling())
    }
  }, [dispatch, shouldPollMetadata])

  useEffect(() => {
    if (shouldPollTasks) {
      dispatch(startTaskPolling())
    } else {
      dispatch(stopTaskPolling())
    }
  }, [dispatch, shouldPollTasks])

  if (!emailIntegration || !contact) {
    return null
  }

  return (
    <>
      <EmailResponseInitialization emailId={email.externalId} />
      <EmailResponsePoller emailId={email.externalId} />
      <>
        <EmailResponseContent email={email} metadata={metadata} contact={contact} emailIntegration={emailIntegration} />
      </>
    </>
  )
}

interface EmailResponseContentProps {
  email: IntegrationEmail
  metadata: EmailMetadata | null
  contact: User
  emailIntegration: ExternalUserServiceIntegration
}

const EmailResponseContent: React.FC<EmailResponseContentProps> = ({ email, metadata, contact, emailIntegration }) => {
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const metadataIsPolling = useAppSelector(selectEmailMetadataPollingEnabled)
  const emailResponseExecuteLoading = useAppSelector(selectEmailResponseExecutionLoading)
  const emailResponseIsPolling = useAppSelector(selectEmailResponsePollingEnabled)
  const emailResponse = useAppSelector(selectEmailResponse)
  const emailResponseLoading = useAppSelector(selectEmailResponseLoading)

  const [collapsed, setCollapsed] = useState<boolean>(false)

  const isNoResult = useMemo(() => isEmailResponseNoResult(emailResponse), [emailResponse])

  const emailResponseGenerating = metadataIsPolling || emailResponseIsPolling || emailResponseExecuteLoading

  if (emailResponseLoading) {
    return <div className='skeleton mt-2' style={{ width: '100%', height: '300px' }} />
  }

  return (
    <>
      <div
        className={classNames('d-flex px-3 pt-4 justify-content-between cursor-pointer', { 'email-container pb-4': collapsed })}
        onClick={() => setCollapsed(!collapsed)}
      >
        <div>
          <div className='d-flex flex-column gap-2'>
            <div className='d-flex gap-2'>
              <UserAvatar
                width='1.5rem'
                height='1.5rem'
                fontSize='0.75rem'
                className='lead-extra-small zocks-text-white zocks-bg-color-gray-900'
                useInitials
                initialLetters={getInitials(getUserDisplayName(currentUser))}
              />
              <div className='d-flex align-items-center paragraph-medium bold'>{getUserDisplayName(currentUser)}</div>
              <UserTag className='bold' userType='current' />
              {!collapsed && emailIntegration && (
                <div className='d-flex align-items-center paragraph-medium-extended zocks-text-gray-600'>{`<${emailIntegration.email}>`}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Collapse in={!collapsed}>
        <div>
          <div className='d-flex flex-column px-3 pb-4 zocks-gap-1.25'>
            <EmailResponseRecipients email={email} contact={contact} emailIntegration={emailIntegration} />
            <div className='zocks-pl-2.0 pe-3'>
              <Container fluid className='p-0'>
                <Row>
                  <Col xs={8}>
                    <div
                      className={classNames('d-flex flex-column gap-3 p-3 zocks-border-1.0 border', {
                        'border-black': !isNoResult,
                        'zocks-bg-color-gray-100 zocks-border-color-primary': isNoResult,
                      })}
                    >
                      <div className='d-flex align-items-center px-3 gap-2'>
                        <i className='bi bi-stars' />
                        <h5>Generate response</h5>
                      </div>
                      {emailResponseGenerating ? (
                        <div className='d-flex w-100 p-3 align-items-center justify-content-center'>
                          <div className='skeleton' style={{ width: '100%', height: '300px' }}>
                            <div className='d-flex flex-column align-items-center justify-content-center h-100'>
                              <div>Generating response…</div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <EmailResponse emailConnector={emailIntegration.serviceId} contact={contact} email={email} metadata={metadata} />
                      )}
                    </div>
                  </Col>
                  <Col xs={4}>
                    <div className='d-flex flex-column gap-3'>
                      <EmailPluginPromo />
                      <EmailResponseChat threadId={email.threadId} />
                      <EmailResponseContacts threadId={email.threadId} />
                      <EmailThreadAttachments emailConnector={emailIntegration.serviceId} threadId={email.threadId} />
                      <EmailResponseTasks emailConnector={emailIntegration.serviceId} threadId={email.threadId} emailId={email.externalId} />
                    </div>
                  </Col>
                </Row>
              </Container>
            </div>
          </div>
        </div>
      </Collapse>
    </>
  )
}

const EmailPluginPromo = () => {
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)

  if (!emailIntegration || emailIntegration.serviceId !== 'outlookMail') {
    return null
  }

  return (
    <PluginPromo
      Icon={<IntegrationIcon serviceId={IntegrationServiceId.OUTLOOK_MAIL} width={32} height={32} />}
      InstallButton={
        <a
          className='button-link-medium outlook-promo-button text-decoration-none p-2 zocks-border-0.25'
          href='https://appsource.microsoft.com/en-us/product/office/WA200008950'
          target='_blank'
          rel='noreferrer noopeener'
        >
          Install Outlook Add-in
        </a>
      }
      header='Bring Zocks to your Outlook'
      body='Access AI responses, client insights, and task tracking directly in your inbox. No switching between apps needed.'
      containerClassname='outlook-promo-container'
      promoType={PromoType.OUTLOOK_MAIL}
    />
  )
}
